<template>
  <section class="board-conversations">
    <div class="table-container">
      <div v-if="isLoading" class="loading-container">
        <span class="loader"></span>
        {{ $t('CALL_PAGE.LIST.LOADING_MESSAGE_TABLE') }}
      </div>
      <ve-table
        :columns="columns"
        :table-data="paginatedList"
        :fixed-header="true"
        :border-around="true"
      >
      </ve-table>
    </div>
    <div class="pagination-container">
      <TableFooter
        :currentPage="currentPage"
        :pageSize="itemsPerPage"
        :totalCount="count"
        @page-change="onPageChange"
      />
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import { VeTable } from 'vue-easytable';
import TableFooter from 'dashboard/components/widgets/TableFooter';
import ConversationApi from '../../../api/inbox/conversation.js';
import CallButton from 'dashboard/components/buttons/CallButton';

export default {
  components: {
    VeTable,
    TableFooter,
    CallButton
  },
  data() {
    return {
      count: 0,
      paginatedList: [],
      itemsPerPage: 15,
      currentPage: 1,
      refreshInterval: null,
      isLoading: false,
      filters: {
        inboxId: null,
        teamId: null,
        agentId: null,
        priority: null,
        lastMessageBy: null,
      },
      availableFiltersData: {
        inboxes: [],
        teams: [],
        users: [],
      },
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      currentRole: 'getCurrentRole',
    }),
    isRestrictedUser() {
      return !['SuperAdmin', 'administrator', 'supervisor', 'team_supervisor'].includes(
        this.currentUser.type || this.currentRole
      );
    },
    columns() {
      return [
        {
          field: 'contactName',
          key: 'contactName',
          title: this.$t('CALL_PAGE.LIST.TABLE_HEADER.NAME'),
          renderBodyCell: ({ row }) => (
            <router-link
              to={`/app/accounts/${this.$route.params.accountId}/contacts/${row.id}`}
              class="contact-name-link"
            >
              {row.contactName}
            </router-link>
          ),
        },
        {
          field: 'phone',
          key: 'phone',
          title: this.$t(
            'CALL_PAGE.LIST.TABLE_HEADER.PHONE_NUMBER'
          ),
        },
        {
          field: 'lastCallDate',
          key: 'lastCallDate',
          title: this.$t(
            'CALL_PAGE.LIST.TABLE_HEADER.LAST_CALL_DATE'
          ),
        },
        {
          field: 'waitingTime',
          key: 'waitingTime',
          title: this.$t(
            'CALL_PAGE.LIST.TABLE_HEADER.WAITING_TIME'
          ),
        },
        {
          field: 'team',
          key: 'team',
          title: this.$t(
            'CALL_PAGE.LIST.TABLE_HEADER.ASSIGNED_TEAM'
          ),
        },
        {
          field: 'assignee',
          key: 'assignee',
          title: this.$t(
            'CALL_PAGE.LIST.TABLE_HEADER.ASSIGNED_AGENT'
          ),
        },
        { 
          field: 'actions', 
          key: 'actions', 
          title: this.$t(
            'CALL_PAGE.LIST.TABLE_HEADER.ACTIONS'
          ),
          renderBodyCell: ({ row,  }) => (
            <CallButton
              currentChat={row.conversation}
              origin="list"
            />
          ),
        }
      ];
    },
    conversationFilters() {
      return {
        inboxId: this.filters.inboxId,
        teamId: this.filters.teamId,
        assigneeId: this.filters.agentId,
        lastMessageBy: this.filters.lastMessageBy,
        conversationType: 'call',
        page: this.currentPage,
        per_page: this.itemsPerPage,
        sortBy: 'last_user_message_at',
      };
    },
  },
  async mounted() {
    if (this.isRestrictedUser) {
      this.filters.agentId = this.currentUser.id;
    }

    await this.loadFilterOptions();

    this.resetAndFetchData();
    this.startAutoRefresh();
  },
  beforeDestroy() {
    this.stopAutoRefresh();
  },
  methods: {
    onFilterApplied(newFilters) {
      this.filters = { ...this.filters, ...newFilters };
      this.resetAndFetchData();
    },
    async resetAndFetchData() {
      this.currentPage = 1;
      await this.fetchCountConversation();
    },
    async fetchCountConversation() {
      this.isLoading = true;
      try {
        const {
          data: { data },
        } = await ConversationApi.get(this.conversationFilters);

        this.count = data.meta.all_count;

        const list = data.payload.map(item => ({
          id: item.meta?.sender?.id,
          contactName: item.meta?.sender?.name || '---',
          phone: item.meta?.sender?.phone_number || '---',
          lastCallDate: item.last_call_date,
          waitingTime: this.formatWaitingTime(item.call_waiting_time_in_seconds) || '00:00:00',
          team: item.meta?.team?.name || '---',
          assignee: item.meta?.assignee?.available_name || '---',
          inbox: item.meta?.inbox?.name || '---',
          meta: {
            inbox: item.meta?.inbox || null,
            team: item.meta?.team || null,
            assignee: item.meta?.assignee || null,
          },
          conversation: item,
        }));

        this.paginatedList = list;

        if (list.length > 0) {
          this.availableFiltersData = {
            inboxes: this.$store.getters['inboxes/getInboxes'],
            teams: this.$store.getters['teams/getTeams'],
            users: this.$store.getters['agents/getAgents'],
          };
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    onPageChange(page) {
      this.currentPage = page;
      this.fetchCountConversation();
    },
    formatDateToLocalTime(dateString) {
      if (!dateString) return '---';

      const isoFormattedDate = dateString.replace(
        /(\d{2})\/(\d{2})\/(\d{4}) (\d{2}:\d{2}:\d{2})/,
        '$3-$2-$1T$4'
      );
      const date = new Date(isoFormattedDate);

      if (isNaN(date.getTime())) return '---';

      const localDate = new Date(date.getTime() - 3 * 3600000);
      const datePart = localDate.toLocaleDateString('pt-BR');
      const timePart = localDate.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      });

      return `${datePart} ${timePart}`;
    },
    truncateText(text, maxLength) {
      if (!text || text.length <= maxLength) return text || '---';
      return text.slice(0, maxLength - 3) + '...';
    },
    formatWaitingTime(seconds) {
      if (!seconds) return '00:00:00';
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      return `${hours.toString().padStart(2, '0')}:${minutes
        .toString()
        .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    },
    startAutoRefresh() {
      this.refreshInterval = setInterval(() => {
        this.fetchCountConversation();
      }, 1 * 60 * 1000); // 1 minuto
    },
    stopAutoRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
      }
    },
    async loadFilterOptions() {
      await this.$store.dispatch('agents/get');
      await this.$store.dispatch('inboxes/get');
      await this.$store.dispatch('teams/get');

      this.availableFiltersData = {
        inboxes: this.$store.getters['inboxes/getInboxes'],
        teams: this.$store.getters['teams/getTeams'],
        users: this.$store.getters['agents/getAgents'],
      };
    },
  },
};
</script>

<style scoped lang="scss">
.priority-urgent {
  background-color: var(--urgent-color);
}

.priority-high {
  background-color: var(--high-color);
}

.priority-medium {
  background-color: var(--medium-color);
}

.priority-low {
  background-color: var(--low-color);
}

.priority-undefined {
  background-color: var(--none-color);
}

.priority-urgent,
.priority-high,
.priority-medium,
.priority-low,
.priority-undefined {
  display: inline-block;
  color: var(--white);
  padding: var(--space-smaller) var(--space-slab);
  border-radius: var(--space-small);
  font-weight: var(--font-weight-bold);
  text-align: center;
}

.board-conversations::v-deep {
  .ve-table {
    thead.ve-table-header .ve-table-header-tr .ve-table-header-th {
      font-size: var(--font-size-mini);
      padding: var(--space-small) var(--space-two);
    }
    tbody.ve-table-body .ve-table-body-tr .ve-table-body-td {
      padding: var(--space-slab) var(--space-two);

      .inbox--name {
        margin: 0;
      }
    }
  }
}

.table-container {
  max-height: calc(100vh - 150px);
  overflow-y: auto;
  overflow-x: auto;
  padding-bottom: 60px;
  flex-grow: 1;
  overflow-y: auto;
}

.pagination-container {
  position: sticky;
  bottom: 0;
  padding: var(--space-slab);
  z-index: 10;
  background-color: var(--white);
}

.board-conversations {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-medium);
  font-weight: bold;
  font-size: var(--font-size-default);
}

.loader {
  border: 4px solid #f3f3f3;
  border-top: 4px solid var(--primary-color);
  border-radius: 50%;
  width: 18px;
  height: 18px;
  animation: spin 1s linear infinite;
  margin-right: 8px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
